/**
 * Email and phone validation (mirrors backend/utils/contact_validation.py).
 */

const EMAIL_RE =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

const INDIAN_MOBILE_RE = /^[6-9]\d{9}$/;
const LANDLINE_RE = /^[2-9]\d{7,11}$/;

export function normalizeEmail(value) {
  return String(value || '')
    .trim()
    .toLowerCase();
}

export function validateEmailParts(value, { required = true } = {}) {
  const email = normalizeEmail(value);
  if (!email) {
    if (required) {
      return { valid: false, email: '', error: 'emailRequired' };
    }
    return { valid: true, email: '', error: null };
  }
  if (email.length > 254 || email.includes('..') || email.startsWith('.') || email.endsWith('.')) {
    return { valid: false, email, error: 'emailInvalid' };
  }
  if ((email.match(/@/g) || []).length !== 1) {
    return { valid: false, email, error: 'emailInvalid' };
  }
  const [, domain] = email.split('@');
  if (!domain || !domain.includes('.')) {
    return { valid: false, email, error: 'emailInvalid' };
  }
  const tld = domain.split('.').pop();
  if (!tld || tld.length < 2) {
    return { valid: false, email, error: 'emailInvalid' };
  }
  if (!EMAIL_RE.test(email)) {
    return { valid: false, email, error: 'emailInvalid' };
  }
  return { valid: true, email, error: null };
}

export function normalizePhone(value) {
  let s = String(value || '').trim();
  if (!s) return '';
  s = s.replace(/[\s\-().]/g, '');
  if (s.startsWith('00')) s = `+${s.slice(2)}`;
  return s;
}

function digitsOnly(normalized) {
  return normalized.replace(/\D/g, '');
}

export function validatePhoneParts(
  value,
  { required = true, allowLandline = false, allowInternational = false } = {},
) {
  const raw = String(value || '').trim();
  if (!raw) {
    if (required) return { valid: false, phone: '', error: 'phoneRequired' };
    return { valid: true, phone: '', error: null };
  }
  const normalized = normalizePhone(raw);
  let digits = digitsOnly(normalized);
  if (digits.startsWith('91') && digits.length >= 12) digits = digits.slice(2);
  if (digits.length === 11 && digits.startsWith('0')) digits = digits.slice(1);

  if (INDIAN_MOBILE_RE.test(digits)) {
    return { valid: true, phone: digits, error: null };
  }
  if (allowLandline && LANDLINE_RE.test(digits)) {
    return { valid: true, phone: digits, error: null };
  }
  if (allowInternational && normalized.startsWith('+') && digits.length >= 8 && digits.length <= 15) {
    return { valid: true, phone: `+${digits}`, error: null };
  }
  return {
    valid: false,
    phone: normalized,
    error: allowLandline ? 'phoneInvalidLandline' : 'phoneInvalid',
  };
}

export function emailErrorMessage(result, t) {
  if (!result?.error) return '';
  const key = `validation.${result.error}`;
  const msg = t(key, { defaultValue: '' });
  if (msg && msg !== key) return msg;
  if (result.error === 'emailRequired') return 'Email is required.';
  return 'Enter a valid email address.';
}

export function phoneErrorMessage(result, t) {
  if (!result?.error) return '';
  const key = `validation.${result.error}`;
  const msg = t(key, { defaultValue: '' });
  if (msg && msg !== key) return msg;
  if (result.error === 'phoneRequired') return 'Phone number is required.';
  if (result.error === 'phoneInvalidLandline') {
    return 'Enter a valid phone number (10-digit mobile starting with 6–9, or a landline).';
  }
  return 'Enter a valid 10-digit Indian mobile number starting with 6, 7, 8, or 9 (e.g. 9876543210).';
}
